import React, { useState, useEffect } from "react";
import { countries } from "../../data/countries";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../supabaseClient";
import ReactCountryFlag from "react-country-flag";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, AlertCircle, Clock, X, Globe } from "lucide-react";

export default function SelectCountries() {
  const navigate = useNavigate();
  const { user } = useApp();

  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [formattedAmount, setFormattedAmount] = useState("");
  const [rate, setRate] = useState(null);
  const [operation, setOperation] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [receivedAmount, setReceivedAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dbRates, setDbRates] = useState([]);
  const [originSupported, setOriginSupported] = useState(true);
  
  // 🔹 Estado para controlar el nuevo Modal de países
  const [showCountryModal, setShowCountryModal] = useState(false);

  // 🔹 Obtener país origen desde user.country
  useEffect(() => {
    if (!user?.country) return;

    const match = countries.find(
      (c) => c.name.toLowerCase() === user.country.toLowerCase()
    );

    if (match) {
      setOrigin(match.code);
      setDestination(countries.find((c) => c.code !== match.code)?.code || "");
    } else {
      setOrigin(null);
      setOriginSupported(false);
    }
  }, [user]);

  // 🔹 Cargar tasas
  useEffect(() => {
    const loadRates = async () => {
      const { data } = await supabase.from("rates").select("*");
      setDbRates(data || []);
    };
    loadRates();
  }, []);

  // 🔹 Verificar si el país origen está soportado
  useEffect(() => {
    if (!origin || !dbRates.length) return;
    const supported = dbRates.some((r) => r.origin_country === origin);
    setOriginSupported(supported);
  }, [origin, dbRates]);

  // 🔹 Formatear input de cantidad
  const handleAmountChange = (value) => {
    const raw = value.replace(/\D/g, "");
    setAmount(raw);

    if (!raw) {
      setFormattedAmount("");
      setReceivedAmount(null);
      return;
    }

    setFormattedAmount(new Intl.NumberFormat("es-CO").format(raw));
  };

  // 🔹 Calcular tasa EXACTA (Sin redondear a enteros)
  useEffect(() => {
    if (!origin || !destination || !amount || !dbRates.length || !originSupported) {
      setRate(null);
      setReceivedAmount(null);
      return;
    }

    const match = dbRates.find(
      (r) => r.origin_country === origin && r.destination_country === destination
    );

    if (!match) {
      setRate(null);
      setReceivedAmount(null);
      return;
    }

    setRate(match.rate);
    setOperation(match.operation);
    setCurrency(match.currency);

    // Cálculo matemático exacto
    let calculated =
      match.operation === "multiply"
        ? parseFloat(amount) * match.rate
        : parseFloat(amount) / match.rate;

    // 🔥 Eliminamos el Math.round() para no perder exactitud
    setReceivedAmount(calculated);
  }, [origin, destination, amount, dbRates, originSupported]);

  const originCountry = countries.find((c) => c.code === origin) || {
    code: "",
    name: user?.country || "No disponible",
    currency: "---",
  };

  const destinationCountry = countries.find((c) => c.code === destination) || {
    code: "",
    name: "Selecciona país",
    currency: "---",
  };

  const canContinue = origin && destination && amount && receivedAmount && originSupported;

  const goNext = async () => {
    if (!user || !canContinue) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("transactions")
        .insert([
          {
            user_id: user.id,
            origin_country: origin,
            destination_country: destination,
            amount: parseFloat(amount),
            rate,
            operation,
            currency,
            received_amount: Number(receivedAmount.toFixed(2)), // Guardamos en BD con máximo 2 decimales
            status: "Creando",
            current_step: "destination",
            created_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;

      navigate("/send/step2", {
        state: {
          origin,
          destination,
          amount: parseFloat(amount),
          rate,
          receivedAmount: Number(receivedAmount.toFixed(2)),
          operation,
          currency,
          transactionId: data.id,
        },
      });
    } catch (err) {
      console.error(err);
      alert("Error creando transacción");
    } finally {
      setLoading(false);
    }
  };

  // Lista de países disponibles para destino (excluyendo el origen)
  const availableDestinations = countries.filter((c) => c.code !== origin);

  return (
    <div className="min-h-screen bg-[#f4f7fb] flex flex-col relative pb-32 pt-6 px-5">
      <div className="max-w-md mx-auto w-full flex-1">
        
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <h1 className="text-3xl md:text-[32px] font-black text-slate-800 tracking-tight leading-tight">
            ¿Cuánto enviarás?
          </h1>
          <p className="text-slate-500 font-medium mt-2">
            Calcula tu envío al instante y sin comisiones ocultas.
          </p>
        </motion.div>

        {/* MENSAJE SI EL PAÍS DE ORIGEN NO ESTÁ SOPORTADO */}
        <AnimatePresence>
          {!originSupported && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 rounded-[24px] border border-red-100 p-5 mb-6 flex gap-4 shadow-sm"
            >
              <div className="bg-white rounded-full p-2 text-red-500 shadow-sm shrink-0 h-fit">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-black text-red-900 text-sm tracking-tight mb-1 uppercase">Servicio no disponible</h3>
                <p className="text-[11px] font-bold text-red-700/80 leading-relaxed">
                  Actualmente no ofrecemos envíos desde tu país de registro. Estamos trabajando para expandirnos pronto.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CONTENEDOR DE LA CALCULADORA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-[32px] shadow-sm border border-slate-100 overflow-hidden mb-6 relative"
        >
          {/* SECCIÓN ORIGEN (ENVÍAS) */}
          <div className="p-5 sm:p-6 pb-8 relative">
            <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Tú envías</h2>
            
            <div className="flex items-center justify-between gap-3">
              <input
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={formattedAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                disabled={!originSupported}
                /* 🔥 Mejorado para móviles: text-2xl base, overflow-hidden y ellipsis para no romper la caja */
                className="w-full text-2xl sm:text-4xl md:text-5xl font-black bg-transparent outline-none text-slate-800 placeholder-slate-200 tracking-tighter disabled:opacity-50 min-w-0 text-ellipsis overflow-hidden"
              />
              
              <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-2xl border border-slate-100 shrink-0">
                {originCountry.code ? (
                  <ReactCountryFlag countryCode={originCountry.code} svg style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} className="rounded-full shadow-sm" />
                ) : (
                  <span className="text-2xl">🌍</span>
                )}
                <span className="font-bold text-slate-700">{originCountry.currency}</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 font-medium mt-1 pl-1 capitalize">{originCountry.name}</p>
          </div>

          {/* DIVISOR VISUAL */}
          <div className="relative h-px bg-slate-100 w-full">
            <div className="absolute left-6 -top-3.5 w-7 h-7 bg-white border border-slate-100 rounded-full flex items-center justify-center shadow-sm z-10">
              <ArrowRight className="w-4 h-4 text-slate-400 rotate-90" />
            </div>
          </div>

          {/* SECCIÓN DESTINO (RECIBEN) */}
          <div className="p-5 sm:p-6 pt-8 bg-slate-50/50">
            <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Ellos reciben</h2>
            
            <div className="flex items-center justify-between gap-3">
              <div className={`text-2xl sm:text-4xl md:text-5xl font-black tracking-tighter truncate min-w-0 ${receivedAmount ? 'text-green-500' : 'text-slate-300'}`}>
                {receivedAmount
                  /* 🔥 Muestra exacto con un MÁXIMO de 2 decimales */
                  ? new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(receivedAmount)
                  : "0"}
              </div>

              {/* Selector Modal */}
              <button
                onClick={() => setShowCountryModal(true)}
                className="flex items-center gap-2 bg-white px-3 py-2 rounded-2xl border border-slate-200 shadow-sm hover:border-blue-300 transition-colors shrink-0 focus:outline-none focus:ring-4 focus:ring-blue-500/20 active:scale-95"
              >
                {destinationCountry.code ? (
                  <ReactCountryFlag countryCode={destinationCountry.code} svg style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} className="rounded-full shadow-sm" />
                ) : (
                  <span className="text-2xl">🌍</span>
                )}
                
                <span className="font-bold text-slate-800 pr-1">
                  {currency || destinationCountry.currency || "---"}
                </span>
              </button>
            </div>
            <p className="text-xs text-slate-500 font-medium mt-1 pl-1 capitalize">{destinationCountry.name}</p>
          </div>
        </motion.div>

        {/* TARJETA DE TASA GARANTIZADA */}
        <AnimatePresence>
          {rate && originSupported && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              /* 🔥 Mejorado para móviles: flex-col en pantallas pequeñas, flex-row en PC */
              className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-[20px] p-4 shadow-sm border border-slate-100 gap-3"
            >
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-blue-600 w-full sm:w-auto justify-center sm:justify-start">
                <Clock className="w-4 h-4 shrink-0" /> Tasa garantizada
              </div>

              <div className="flex items-center justify-center gap-2 font-bold text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-xl border border-slate-100 w-full sm:w-auto">
                <ReactCountryFlag countryCode={originCountry.code} svg style={{ width: '16px', height: '16px', borderRadius: '50%', objectFit: 'cover' }} />
                <span>1 {originCountry.currency}</span>
                <span className="text-slate-300 mx-1">=</span>
                {/* 🔥 Formato de tasa también optimizado */}
                <span className="text-green-600 truncate">{new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(rate)} {destinationCountry.currency}</span>
                <ReactCountryFlag countryCode={destinationCountry.code} svg style={{ width: '16px', height: '16px', borderRadius: '50%', objectFit: 'cover' }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* BOTÓN FLOTANTE */}
      <div className="fixed bottom-0 left-0 w-full bg-gradient-to-t from-[#f4f7fb] via-[#f4f7fb] to-transparent pt-12 pb-6 px-5 z-20 pointer-events-none">
        <div className="max-w-md mx-auto pointer-events-auto">
          <button
            onClick={goNext}
            disabled={!canContinue || loading}
            className={`w-full py-5 px-6 rounded-[20px] text-[15px] font-black uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 active:scale-[0.98]
              ${canContinue && !loading
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-[0_15px_30px_rgba(37,99,235,0.3)]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
              }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-[3px] border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              'Continuar Envío'
            )}
          </button>
        </div>
      </div>

      {/* ================== MODAL DE PAÍSES (Bottom Sheet) ================== */}
      <AnimatePresence>
        {showCountryModal && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/40 backdrop-blur-sm md:p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0"
              onClick={() => setShowCountryModal(false)}
            />
            
            <motion.div 
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="bg-white w-full max-w-md rounded-t-[32px] md:rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[85vh] relative z-10"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Globe className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-800 text-lg leading-tight">País destino</h3>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-0.5">Selecciona hacia dónde envías</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCountryModal(false)}
                  className="w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-2">
                {availableDestinations.length === 0 ? (
                  <p className="text-center text-slate-500 py-6 font-bold">No hay países disponibles.</p>
                ) : (
                  availableDestinations.map((country) => (
                    <button
                      key={country.code}
                      onClick={() => {
                        setDestination(country.code);
                        setShowCountryModal(false);
                      }}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all active:scale-[0.98] ${
                        destination === country.code 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'bg-white border-slate-100 hover:border-blue-100 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <ReactCountryFlag 
                          countryCode={country.code} 
                          svg 
                          style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} 
                          className="rounded-full shadow-sm" 
                        />
                        <div className="text-left">
                          <p className={`font-black ${destination === country.code ? 'text-blue-900' : 'text-slate-800'}`}>
                            {country.name}
                          </p>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Moneda: {country.currency}
                          </p>
                        </div>
                      </div>
                      
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        destination === country.code ? 'border-blue-600 bg-blue-600' : 'border-slate-300'
                      }`}>
                        {destination === country.code && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

